import React, { useEffect, useState, useRef } from "react";
import "./Success.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BsCheck } from "react-icons/bs";
import { BsXCircle } from "react-icons/bs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";

export default function PaymentSuccessCashfree() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const view = "receipt";
  const receiptRef = useRef(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cfOrderId = searchParams.get("order_id");

    if (!cfOrderId) {
      alert("Invalid order ID.");
      navigate("/stalls");
      return;
    }

    let isSubscribed = true;
    let pollInterval = null;
    const verifyEndpoint = `https://admin-aged-field-2794.fly.dev/orders/verify-payment/cashfree/${cfOrderId}`;
    const byCashfreeEndpoint = `https://admin-aged-field-2794.fly.dev/orders/by-cashfree/${cfOrderId}`;

    const verifyAndFetchOrder = async () => {
      try {
        // Trigger verification, but ignore transient errors (e.g. amount not yet settled) — just keep polling
        await axios.get(verifyEndpoint).catch(() => {});

        const res = await axios.get(byCashfreeEndpoint);
        if (!isSubscribed) return;

        setOrderDetails(res.data);

        if (res.data.payment_status === "SUCCESS") {
          localStorage.removeItem("cartItems");
          if (pollInterval) clearInterval(pollInterval);
        } else if (res.data.payment_status === "FAILED") {
          if (pollInterval) clearInterval(pollInterval);
        }
      } catch (err) {
        // Quiet error during transient network pause; keep polling
      }
    };

    verifyAndFetchOrder();
    pollInterval = setInterval(verifyAndFetchOrder, 2000);

    return () => {
      isSubscribed = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [location.search, navigate]);

  const downloadPDF = () => {
    const input = receiptRef.current;
    if (!input) return;

    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`receipt_${orderDetails.id.slice(0, 6)}.pdf`);

      setTimeout(() => navigate("/stalls"), 1000);
    });
  };

  if (!orderDetails) {
    return <p className="loading-text">Verifying Payment...</p>;
  }

  // ✅ IMPORTANT CHECK
  const isPaymentSuccessful =
    orderDetails.payment_status === "SUCCESS" &&
    orderDetails.payment_verified === true;

  // ❌ If payment failed or pending → show failure screen
  if (!isPaymentSuccessful) {
    return (
      <div className="receipt-wrapper">
        <div className="status-wrapper">
          <p className="failed-status" style={{ color: "red", fontWeight: "bold" }}>
            <span style={{ fontSize: "22px", marginRight: "8px" }}>
              <BsXCircle />
            </span>
            Payment Failed or Pending
          </p>
          <button
            style={{ marginTop: "20px" }}
            onClick={() => navigate("/stalls")}
          >
            Back to Stalls
          </button>
        </div>
      </div>
    );
  }

  // ✅ Only reach here if payment is SUCCESS
  const tokenNo = orderDetails.token_number ?? orderDetails.id.slice(0, 4);

  const createdAt = new Date(
    orderDetails.created_datetime
  ).toLocaleString("en-IN", {
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  const totalCgst = orderDetails.cgst ?? 0;
  const totalSgst = orderDetails.sgst ?? 0;
  const totalGst = orderDetails.total_gst ?? totalCgst + totalSgst;
  const roundOff = orderDetails.round_off ?? 0;
  const grandTotal = orderDetails.total_amount ?? 0;
  const subtotal = grandTotal - roundOff;

  return (
    <div className="receipt-wrapper">
      <div className="payment-success-header">
        <div className="success-checkmark-glow">
          <span className="success-checkmark-icon">
            <BsCheck />
          </span>
        </div>
        <h1 className="success-title">Payment Successful</h1>
        <p className="success-subtitle">Thank you for your order!</p>
      </div>

      {/* <div className="toggle-btns">
        <button
          className={`toggle-btn ${view === "receipt" ? "active" : ""}`}
          onClick={() => setView("receipt")}
        >
          Show Receipt
        </button>
        <button
          className={`toggle-btn ${view === "qr" ? "active" : ""}`}
          onClick={() => setView("qr")}
        >
          Show QR
        </button>
      </div> */}

      {view === "receipt" && (
        <>
          <div className="receipt-card compact-token" ref={receiptRef}>
            <h2 className="stall-name">
              {orderDetails.order_details[0]?.stall_name || "Stall Name"}
            </h2>

            <div className="token-hero-badge">
              <span className="token-hero-label">YOUR TOKEN NUMBER</span>
              <h3 className="token-hero-number">{tokenNo}</h3>
            </div>

            <p className="order-date">GSTIN: 36AAVFN1793L1ZE</p>
            <p className="order-date">Date: {createdAt}</p>

            <hr className="separator" />

            <div className="token-table">
              <div className="token-header">
                <span>Item</span>
                <span>Rs</span>
              </div>

              {orderDetails.order_details.map((item, index) => (
                <div key={index} className="token-row">
                  <span className="item-name">
                    {item.name} × {item.quantity}
                  </span>
                  <span>{item.price.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="token-summary">
              <p>
                <span>CGST</span>
                <span>{totalCgst.toFixed(2)}</span>
              </p>
              <p>
                <span>SGST</span>
                <span>{totalSgst.toFixed(2)}</span>
              </p>
              <p>
                <span>Total GST</span>
                <span>{totalGst.toFixed(2)}</span>
              </p>
              <p>
                <span>Total</span>
                <span>{subtotal.toFixed(2)}</span>
              </p>
              <p>
                <span>Round Off</span>
                <span>
                  {roundOff >= 0
                    ? `+${roundOff.toFixed(2)}`
                    : roundOff.toFixed(2)}
                </span>
              </p>
              <div className="separator" style={{ margin: "10px 0" }}></div>
              <p className="grand-total">
                <span>Grand Total</span>
                <span>₹ {grandTotal.toFixed(2)}</span>
              </p>
            </div>
          </div>

          <button className="download-btn" onClick={downloadPDF}>
            Download Receipt
          </button>
        </>
      )}

      {/* {view === "qr" && (
        <div className="qr-wrapper">
          <QRCodeCanvas value={JSON.stringify(orderDetails)} size={180} />
        </div>
      )} */}

      {/* 🔙 Back to Stalls */}
      <button
        className="back-to-stalls-btn"
        onClick={() => {
          localStorage.removeItem("cartItems");
          navigate("/stalls", { replace: true });
        }}
      >
        Back to Stalls
      </button>
    </div>
  );
}
